import {useCallback} from "react"
import {computed} from "@preact/signals-react"
import {useAppToast} from "@ui-seeker/app"
import {postSubmit, getSubmit} from "@ui-seeker/utils"

import {BACKEND_APIS} from "@constants"
import {isCreateBatchDrawerOpen, isCreatingBatch, showCreatingBatchModal, createBatchFormData, isLoadingBatches} from "../@signals"

// Computed signals for validation (reactive to form data changes)
const isValidComputed = computed(() => {
	const {batchType, batchName, customerID, markets, sites, brands, dateStart, dateEnd, topVentas, tags, splitByTargetSites} = createBatchFormData.value

	// Required: Batch Name
	if (!batchName || !batchName.trim()) return false

	if (batchType === 'raw2sku') {
		// For raw2sku: Date Range is required (Markets/Sites are optional)
		if (!dateStart || !dateEnd) return false
		// Validate dateEnd >= dateStart
		if (dateStart > dateEnd) return false
	} else if (batchType === 'raw2raw') {
		// For raw2raw: Customer plus a brand or a tag are required. The batch has to be scoped
		// to a subset of the catalog — either by canonical brand or by tag — so we don't let
		// users create a whole-catalog batch from here.
		if (!customerID) return false
		const hasBrands = brands && brands.length > 0
		const hasTags = tags && tags.length > 0
		if (!hasBrands && !hasTags) return false
	} else if (batchType === 'war2war') {
		// For war2war only the customer is required: brands default to the ones with
		// raw2raw history and the sites to the customer's competitor/own sets, all
		// resolved in ia-normalizer so there is one place that decides the scope.
		if (!customerID) return false
	} else {
		// For sku2raw: Customer required (Brands and SKUs are optional filters)
		if (!customerID) return false
	}

	return true
})

const validationErrorComputed = computed(() => {
	const {batchType, batchName, customerID, markets, sites, brands, dateStart, dateEnd, topVentas, tags, splitByTargetSites} = createBatchFormData.value

	if (!batchName || !batchName.trim()) {
		return "Batch Name is required"
	}

	if (batchType === 'raw2sku') {
		// For raw2sku: Date Range is required (Markets/Sites are optional filters)
		if (!dateStart || !dateEnd) {
			return "Date range is required for Product to SKU batches"
		}
		if (dateStart > dateEnd) {
			return "End date must be greater than or equal to start date"
		}
	} else if (batchType === 'raw2raw') {
		// For raw2raw: Customer is always required, plus at least a brand or a tag.
		if (!customerID) {
			return "Customer is required"
		}
		if ((!brands || brands.length === 0) && (!tags || tags.length === 0)) {
			return "At least one brand or one tag is required for Product to Product batches"
		}
	} else if (batchType === 'war2war') {
		if (!customerID) {
			return "Customer is required"
		}
	} else {
		// For sku2raw: Customer required (Brands and SKUs are optional filters)
		if (!customerID) {
			return "Customer is required"
		}
	}

	return null
})


export default function useCreateBatch() {
	const {showToast} = useAppToast()

	// Use computed signals for validation
	const isValid = isValidComputed.value
	const validationError = validationErrorComputed.value

	const handleClose = useCallback(() => {
		// Reset form data
		createBatchFormData.value = {
			batchType: 'sku2raw',
			batchName: '',
			markets: [],
			sites: [],
			ownSites: [],
			firstDateDays: 30,
			customerID: null,
			brands: [],
			categories: [],
			skus: [],
			dateStart: null,
			dateEnd: null,
			dateField: 'end_date',
			splitByBrands: false,
			splitBySites: false,
			topVentas: false,
			tags: [],
			retryNotFound: false,
			splitByTargetSites: true,
			maxDaysSinceSeen: 90,
			groupBy: null,
			autoValidation: { minRateAutovalidate: null, minGapSecondCandidate: null, maxRateAutoexclude: null },
			autoSecondPass: { enabled: false, model: 'gpt-5.6-luna', threshold: null, nfThreshold: null, samplePct: 0, speedMode: false }
		}
		isCreateBatchDrawerOpen.value = false
	}, [])

	const handleCreate = useCallback(async () => {
		// Read current validation state
		if (!isValidComputed.value) {
			showToast.error("Please fill all required fields")
			return
		}

		// IMPORTANT: Read ALL form values BEFORE closing drawer (closing may trigger effects that reset form)
		const {batchType, batchName, customerID, sites, brands, categories, markets, dateStart, dateEnd, dateField, splitByBrands, splitBySites, skus, autoValidation, groupBy, topVentas, tags, retryNotFound, splitByTargetSites, maxDaysSinceSeen, autoSecondPass} = createBatchFormData.value

		// Close drawer and show blocking modal
		isCreateBatchDrawerOpen.value = false
		showCreatingBatchModal.value = true
		isCreatingBatch.value = true

		try {
			// For raw2sku with markets selected, fetch sites for those markets first
			let finalSites = sites || []
			if (batchType === 'raw2sku' && markets && markets.length > 0 && (!sites || sites.length === 0)) {
				// Fetch all sites for the selected markets
				const marketCodes = markets.map(m => m.value || m).join(',')
				const sitesResponse = await getSubmit(`/api/products-review/sites?markets=${marketCodes}`)
				if (sitesResponse && Array.isArray(sitesResponse)) {
					finalSites = sitesResponse.map(s => ({ value: s.value, label: s.label }))
				}

				if (finalSites.length === 0) {
					throw new Error(`No sites found for markets ${marketCodes}`)
				}
			}

			// Helper to extract IDs from objects with {value, label} format
			const extractIds = (items) => {
				if (!items || !Array.isArray(items)) return []
				return items.map(item => item?.value ?? item).filter(id => id != null)
			}
			const extractId = (item) => item?.value ?? item

			const payload = {
				batchType: batchType,
				batchName: batchName.trim(),
				customerID: extractId(customerID),
				sites: extractIds(finalSites),
				brands: extractIds(brands),
				maxDaysSinceSeen: maxDaysSinceSeen || 90,
				retryNotFound: !!retryNotFound
			}

			// Add type-specific fields
			if (batchType === 'raw2sku') {
				// Convert YYYY-MM-DD to YYYYMMDD
				payload.dateStart = dateStart ? dateStart.replace(/-/g, '') : null
				payload.dateEnd = dateEnd ? dateEnd.replace(/-/g, '') : null
				payload.dateField = dateField || 'end_date'
				payload.splitByBrands = splitByBrands || false
				payload.splitBySites = splitBySites || false
				payload.categories = extractIds(categories)
			} else if (batchType === 'sku2raw') {
				payload.id_sku = extractIds(skus)
				payload.categories = extractIds(categories)
			} else if (batchType === 'war2war') {
				// Anchors are NEW competitor listings; the candidates are the own catalog.
				// Everything here is an optional narrowing — ia-normalizer fills the gaps.
				payload.ownSites = extractIds(createBatchFormData.value.ownSites)
				payload.firstDateDays = createBatchFormData.value.firstDateDays || 30
			} else if (batchType === 'raw2raw') {
				// customerID + brands + optional sites already in base payload; add raw2raw filters
				payload.topVentas = !!topVentas
				payload.tags = extractIds(tags)
				// The batch is always scoped by brand or tag (validation), so split by site
				// stays a plain operator choice.
				payload.splitBySites = splitByTargetSites !== false
			}

			// Auto AI second pass on batch finish (any queue type; calibrated
			// defaults are per type — raw2sku is stricter: 0.9/0.95)
			if (autoSecondPass?.enabled) {
				const d = batchType === 'raw2sku' ? { t: 0.9, nf: 0.95 } : { t: 0.8, nf: 0.9 }
				const nf = autoSecondPass.nfThreshold
				payload.autoSecondPass = {
					model: autoSecondPass.model || 'gpt-5.6-luna',
					threshold: parseFloat(autoSecondPass.threshold) || d.t,
					nf_threshold: nf === '' ? null : (nf == null ? d.nf : parseFloat(nf)),
					sample_rate: (parseFloat(autoSecondPass.samplePct) || 0) / 100,
					verify_images: autoSecondPass.verifyImages !== false,
					// Transport: OpenAI Batch API by default (~50% cheaper, decisions
					// land when OpenAI resolves the batch — up to 24h). Speed mode
					// falls back to the immediate per-row run.
					execution_mode: autoSecondPass.speedMode ? 'sync' : 'batch'
				}
			}

			// Include batch-level auto-validation thresholds only if any value was customized
			if (autoValidation) {
				const customized = {}
				if (autoValidation.minRateAutovalidate !== null && autoValidation.minRateAutovalidate !== '') {
					customized.minRateAutovalidate = parseFloat(autoValidation.minRateAutovalidate)
				}
				if (autoValidation.minGapSecondCandidate !== null && autoValidation.minGapSecondCandidate !== '') {
					customized.minGapSecondCandidate = parseFloat(autoValidation.minGapSecondCandidate)
				}
				if (autoValidation.maxRateAutoexclude !== null && autoValidation.maxRateAutoexclude !== '') {
					customized.maxRateAutoexclude = parseFloat(autoValidation.maxRateAutoexclude)
				}
				if (Object.keys(customized).length > 0) {
					payload.autoValidation = customized
				}
			}

			// Include groupBy if set
			if (groupBy) {
				payload.groupBy = groupBy
			}

			// Make request
			const response = await postSubmit(`${BACKEND_APIS.BATCH_ADMIN}/admin/batches`, payload)

			// Success
			showCreatingBatchModal.value = false
			isCreatingBatch.value = false

			// Reset form
			createBatchFormData.value = {
				batchType: 'sku2raw',
				batchName: '',
				markets: [],
				sites: [],
				ownSites: [],
				firstDateDays: 30,
				customerID: null,
				brands: [],
				skus: [],
				dateStart: null,
				dateEnd: null,
				dateField: 'end_date',
				splitByBrands: false,
				splitBySites: false,
				topVentas: false,
				tags: [],
				retryNotFound: false,
				splitByTargetSites: true,
				autoValidation: { minRateAutovalidate: null, minGapSecondCandidate: null, maxRateAutoexclude: null }
			}

			// Refresh the batches list
			isLoadingBatches.value = true

			// Show success message based on response
			if (response?.batchesCreated && response.batchesCreated > 1) {
				showToast.success(`${response.batchesCreated} batches created successfully!`)
			} else {
				showToast.success('Batch created successfully!')
			}
		} catch (err) {
			// Error - close modal and show error
			showCreatingBatchModal.value = false
			isCreatingBatch.value = false

			const errorMessage = err?.message || 'Failed to create batch'
			showToast.error(`Error: ${errorMessage}`)

			// Reopen drawer so user can retry
			isCreateBatchDrawerOpen.value = true
		}
	}, [showToast])

	return {
		isValid,
		validationError,
		handleClose,
		handleCreate
	}
}
